import { useCallback, useEffect, useState } from "react";
import { Platform } from "react-native";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { useAuth0 } from "@auth0/auth0-react";
// @ts-ignore
import EventBus from "react-native-event-bus";
import Auth0 from "../../../src/components/authProvider/Auth0";
import Config from "react-native-config";

import {
  LOGIN_ROUTE,
  AUTH_WEBVIEW_ROUTE,
  POLICY_LISTING_ROUTE,
} from "../../../src/navigation/NavigationConstants";
import CheckSession from "@lib/utilities/CheckSession";
import { Logger } from "@lib/utilities/HelperFunctions";
import { useTokenManagement } from "./useTokenManagement";
import Constants from "@lib/utilities/Constants";
import Crashlytics from "@lib/utilities/crashlyticsUtils/CrashlyticsUtils";
import { messages } from "../../../src/resources/strings";
import { useAppSelector } from "@lib";

interface LoginFlowReturn {
  errorHandle: string;
  isLoading: boolean;
  handleLogin: () => void;
  setErrorHandle: (error: any) => void;
}

export const useLoginFlow = (): LoginFlowReturn => {
  const [errorHandle, setErrorHandle] = useState(messages.error.noError);
  const [isLoading, setLoading] = useState(false);
  const { isConnected } = useAppSelector((state) => state.userReducer);

  const navigation = useNavigation();
  const { loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const { saveTokensAndNavigate } = useTokenManagement();

  const getAccessToken = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: "openid email profile offline_access",
        },
      });
      await saveTokensAndNavigate(token);
    } catch (error) {
      Logger(error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, saveTokensAndNavigate]);

  useFocusEffect(
    useCallback(() => {
      Crashlytics.logScreen(LOGIN_ROUTE);
      const checkUserToken = async () => {
        try {
          setLoading(true);
          const session = await CheckSession();
          if (session) {
            navigation.navigate(POLICY_LISTING_ROUTE as never);
          }
        } catch (error) {
          Logger(error);
        } finally {
          setLoading(false);
        }
      };
      checkUserToken();
    }, [navigation]),
  );

  useEffect(() => {
    if (Platform.OS === "web") {
      const listener = () => getAccessToken();
      const eventBus = EventBus.getInstance();
      eventBus.addListener(Constants.Events.LOGIN_SUCCESS, listener);
      return () => eventBus.removeListener(listener);
    }
  }, [getAccessToken]);

  const handleMobileLogin = async (auth0: any) => {
    setLoading(true);
    const credentials = await auth0.webAuth.authorize(
      {
        scope: "openid email profile offline_access",
        audience: `https://${Config.REACT_APP_EXZEO_ID_CLIENT_ID}`,
      },
      { ephemeralSession: true },
    );
    await saveTokensAndNavigate(
      credentials.accessToken,
      credentials.refreshToken,
    );
  };

  const auth0Login = useCallback(async () => {
    const auth0Credentials = {
      domain: Config.REACT_APP_EXZEO_ID_DOMAIN ?? "",
      clientId: Config.REACT_APP_EXZEO_ID_CLIENT_ID ?? "",
    };
    try {
      const auth0 = new (Auth0 as any)(auth0Credentials);
      await handleMobileLogin(auth0);
    } catch (error) {
      console.log(error);
      setLoading(false);
      Logger("ERROR IN MOBILE AUTH:" + JSON.stringify(error));
    }
  }, [saveTokensAndNavigate]);

  const handleLogin = useCallback(async () => {
    if (!isConnected) {
      setErrorHandle(messages.error.youAreOffline);
      return;
    }
    setErrorHandle(messages.error.noError);
    if (Platform.OS === "web") {
      setLoading(true);
      loginWithRedirect()
        .then(getAccessToken)
        .catch((err: unknown) => {
          Logger("Login error:" + err);
          setLoading(false);
        });
    } else {
      if (Platform.OS === "ios") {
        await auth0Login();
      } else {
        navigation.navigate(AUTH_WEBVIEW_ROUTE as never);
      }
    }
  }, [isConnected, getAccessToken, auth0Login]);

  return {
    errorHandle,
    isLoading,
    handleLogin,
    setErrorHandle,
  };
};
